import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CommandPalette } from './CommandPalette.jsx';

export const CommandPaletteContext = createContext(null);

export const useCommandPalette = () => {
    const ctx = useContext(CommandPaletteContext);
    if (!ctx) throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
    return ctx;
};

export const CommandPaletteProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    // The navbar registers itself while mounted; the Ctrl/Cmd+K shortcut is
    // only wired up while it is on screen. Pre-login/public pages (e.g. the
    // Home Page) render no navbar, so the shortcut stays inert there.
    const [navbarCount, setNavbarCount] = useState(0);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen(v => !v), []);

    const registerNavbar = useCallback(() => {
        setNavbarCount(c => c + 1);
        return () => setNavbarCount(c => Math.max(0, c - 1));
    }, []);

    const shortcutEnabled = navbarCount > 0;

    useEffect(() => {
        if (!shortcutEnabled) return;
        const onKeyDown = (e) => {
            const isCmdK = (e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K');
            if (isCmdK) {
                e.preventDefault();
                toggle();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [shortcutEnabled, toggle]);

    return (
        <CommandPaletteContext.Provider value={{ isOpen, open, close, toggle, registerNavbar }}>
            {children}
            {isOpen && <CommandPalette onClose={close} />}
        </CommandPaletteContext.Provider>
    );
};
